import { Button, Stack, Text } from '@chakra-ui/react'
import { faCancel, faFlag } from '@fortawesome/pro-regular-svg-icons'
import { faVideo } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'

import { getSessionJoinRedirectUrl } from '@/client/services/url'

import type { PostDetails } from '@/components/Community/Post/types'
import ReportModalV2 from '@/components/Report/ReportModalV2'
import ShwLink from '@/components/ShwLink'
import Divider from '@/components/Worlds/UI/Divider'

import CancelGivingLiveHelpModal from './CancelGivingLiveHelpModal'
import { LIVE_HELP_LEARNER_TIMEOUT_MINUTES } from './constants'
import LiveHelpSessionLabel from './LiveHelpSessionLabel'
import type { LiveHelpRequestWithSessionDetails } from './types'

export default function TutorGivingLiveHelpBanner({
  post,
  liveHelpRequest,
  postIsFullView,
}: {
  post: PostDetails
  liveHelpRequest: LiveHelpRequestWithSessionDetails
  postIsFullView: boolean
}): JSX.Element {
  const [showCancelGivingHelpModal, setShowCancelGivingHelpModal] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)

  const { user } = post
  const { session } = liveHelpRequest

  return (
    <>
      <CancelGivingLiveHelpModal
        postId={post.id}
        open={showCancelGivingHelpModal}
        onClose={() => setShowCancelGivingHelpModal(false)}
      />
      <Stack
        direction="column"
        width="100%"
        p="16px"
        bgColor="bg.subtle"
        borderRadius="8px"
        spacing="16px"
      >
        <ReportModalV2
          entity={{ type: 'post', id: post.id }}
          open={showReportModal}
          onClose={() => setShowReportModal(false)}
          reportedUserName={user.displayName}
        />
        <Stack direction="row" justify="space-between" align="left">
          <LiveHelpSessionLabel user={user} />
          <Stack
            direction={{ base: 'column', xl: postIsFullView ? 'row' : 'column' }}
            flexShrink={0}
            spacing={{ base: 0, xl: postIsFullView ? '24px' : 0 }}
            alignItems="flex-start"
          >
            <ShwLink href={getSessionJoinRedirectUrl(session.id, true)} isExternal>
              <Button variant="ghost" leftIcon={<FontAwesomeIcon icon={faVideo} />}>
                Join Zoom Room
              </Button>
            </ShwLink>
            <Button
              variant="ghost"
              onClick={() => setShowReportModal(true)}
              leftIcon={<FontAwesomeIcon icon={faFlag} />}
            >
              Share Safety Concern
            </Button>
            <Button
              onClick={() => setShowCancelGivingHelpModal(true)}
              variant="danger-outline"
              leftIcon={<FontAwesomeIcon icon={faCancel} />}
            >
              Cancel Giving Help
            </Button>
          </Stack>
        </Stack>

        <Divider />

        <Text textAlign="center">
          Please allow {LIVE_HELP_LEARNER_TIMEOUT_MINUTES} minutes for {user.displayName} to join
          the room. After {LIVE_HELP_LEARNER_TIMEOUT_MINUTES} minutes, feel free to cancel and help
          someone else instead.
        </Text>
      </Stack>
    </>
  )
}
